import { RepublishOutcome, RepublishResponse } from '@/console/api/types'

/**
 * MASS `반품 정산 재발행` 패널의 순수 로직. 계산은 전부 여기 두고 `RepublishPanel` 은 이 모듈의
 * 결과만 그린다.
 *
 * 핸드오프 §4.6 함정: `inboundReportIds` 는 `UI_GRREPORT_DETAIL.id` 다(≤300건). `reason` 은
 * ≤500자·제어문자 불가. 서버는 클레임번호 중복을 검사하지 않는다 — 화면은 범위 조회 UI 를
 * 만들지 않고 ID 직접 입력만 받는다(이중청구 위험).
 */
export const MAX_REPUBLISH_IDS = 300
export const MAX_REASON_LENGTH = 500

export type ParsedReportIds = { ids: number[]; invalid: string[]; duplicates: number; overLimit: boolean }

/** 쉼표·줄바꿈을 구분자로 보고, distinct(입력 순서 유지) 후 1 이상의 안전 정수가 아닌 값은 `invalid` 로 따로 모은다. */
export const parseInboundReportIds = (raw: string): ParsedReportIds => {
  const pieces = raw
    .split(/[,\n]/)
    .map((piece) => piece.trim())
    .filter((piece) => piece.length > 0)

  const seen = new Set<number>()
  const ids: number[] = []
  const invalid: string[] = []
  let duplicates = 0

  pieces.forEach((piece) => {
    // LOGISPRD-13362 — F-OPT `parseStandardOrderIds` 와 같은 기준. `UI_GRREPORT_DETAIL.id` 는
    // auto-increment PK 라 음수가 없고, 안전 정수 범위를 넘으면 JS 가 값을 뭉개 엉뚱한 건을 보낸다.
    if (!/^\d+$/.test(piece)) {
      invalid.push(piece)
      return
    }

    // PR #795 리뷰 — auto-increment PK 라 0 은 없다. 보내 봐야 NOT_FOUND 라 화면에서 끊는다.
    const id = Number(piece)
    if (id <= 0 || !Number.isSafeInteger(id)) {
      invalid.push(piece)
      return
    }

    if (seen.has(id)) {
      duplicates += 1
      return
    }

    seen.add(id)
    ids.push(id)
  })

  return { ids, invalid, duplicates, overLimit: ids.length > MAX_REPUBLISH_IDS }
}

// LOGISPRD-13362 — BE `REASON_NO_CONTROL_CHARS_REGEX` 가 막는 U+0085·U+2028·U+2029 까지 함께 막는다
// (서버 400 을 화면에서 먼저 끊는다). U+007F 는 서버는 받지만 감사 로그에 남길 이유가 없어 계속 막는다.
// eslint-disable-next-line no-control-regex -- 제어문자 존재 자체를 검사하는 게 목적이다.
const CONTROL_CHAR_PATTERN = /[\x00-\x1F\x7F\x85\u2028\u2029]/

/** 사유는 필수 · 500자 이하 · 제어문자 불가. 문제가 없으면 `null`. */
export const validateReason = (reason: string): string | null => {
  if (reason.trim().length === 0) {
    return '사유는 필수예요.'
  }

  if (reason.length > MAX_REASON_LENGTH) {
    return `사유는 ${MAX_REASON_LENGTH}자를 넘을 수 없어요.`
  }

  if (CONTROL_CHAR_PATTERN.test(reason)) {
    return '사유에 제어문자(줄바꿈·탭 등)가 있어요.'
  }

  return null
}

export type RepublishTone = 'good' | 'neutral' | 'warn' | 'crit'

/** 결과 집계 — 6개 지표를 고정 순서로 돌려준다. */
export const summarizeRepublish = (res: RepublishResponse): { key: string; label: string; value: number; tone: RepublishTone }[] => [
  { key: 'requested', label: '요청', value: res.requested, tone: 'neutral' },
  { key: 'duplicatesIgnored', label: '중복 제외', value: res.duplicatesIgnored, tone: 'warn' },
  { key: 'published', label: '발행', value: res.published, tone: 'good' },
  { key: 'skipped', label: '건너뜀', value: res.skipped, tone: 'neutral' },
  { key: 'notFound', label: '없음', value: res.notFound, tone: 'warn' },
  { key: 'failed', label: '실패', value: res.failed, tone: 'crit' },
]

/** 건별 결과 `Tag` 톤. PUBLISHED=good, SKIPPED=neutral, NOT_FOUND=warn, FAILED=crit. */
export const republishOutcomeTone = (outcome: RepublishOutcome): RepublishTone => {
  switch (outcome) {
    case 'PUBLISHED':
      return 'good'
    case 'NOT_FOUND':
      return 'warn'
    case 'FAILED':
      return 'crit'
    case 'SKIPPED':
      return 'neutral'
    default: {
      // PR #795 리뷰 — BE 에 outcome 이 새로 생기면 여기서 컴파일 오류로 드러나게 한다.
      const unknownOutcome: never = outcome
      return unknownOutcome
    }
  }
}
